import { generatePath, matchPath } from "react-router-dom";
import {
  PUBLIC_ROUTE_PATHS,
  SHOW_EXHIBITOR_QUICK_CART_PATH,
} from "view/routes";

type ComposedQuickOrderURLParams = {
  showId: string;
  exhibitorPairId: string;
  orderId: string;
};

export const quickOrderIdParamName = "quickOrderId";

/**
 * True when `pathname` (defaults to the current URL) is a public /
 * unauthenticated route declared in `PUBLIC_ROUTE_PATHS`.
 *
 * Uses the pure `matchPath` (no router hooks) on purpose, so it is safe to
 * call from components that may render outside the data router — e.g. the
 * global `VersionModal`, which mounts `FfairModal` outside `<RouterProvider>`.
 * There `useMatches()` / `useLocation()` would throw.
 */
export function isPublicRoute(
  pathname: string = window.location.pathname
): boolean {
  return PUBLIC_ROUTE_PATHS.some((path) => matchPath(path, pathname) !== null);
}

/**
 * Reads the post-login redirect target from a query string
 * (`?redirectPathAfterLogin=...`, legacy alias `?redirectPath=...`).
 *
 * `URLSearchParams.get()` already percent-decodes the value — do NOT run
 * `decodeURIComponent` on top of it: a second decode corrupts targets whose
 * own query contains encoded characters (`%26` → `&` splits it) and throws
 * `URIError` on a lone `%`.
 *
 * Only internal paths are accepted (must start with a single `/`) so the
 * parameter cannot be abused as an open redirect.
 */
export function getRedirectPathAfterLogin(
  search: string = window.location.search
): string {
  const params = new URLSearchParams(search);
  const raw =
    params.get("redirectPathAfterLogin") || params.get("redirectPath") || "";
  return raw.startsWith("/") && !raw.startsWith("//") ? raw : "";
}

/**
 * Appends `ignoreStandardRedirect=true` with the correct separator. Blindly
 * appending `&` to a path without a query string used to glue the parameter
 * into the PATHNAME (`/foo&ignoreStandardRedirect=true`), which matched no
 * route and let the role router's `*` fallback dump the user on `/`.
 */
export function appendIgnoreStandardRedirect(path: string): string {
  return `${path}${path.includes("?") ? "&" : "?"}ignoreStandardRedirect=true`;
}

export function composeQuickOrderURL({
  showId,
  orderId,
  exhibitorPairId,
}: ComposedQuickOrderURLParams) {
  return new URL(
    `${generatePath(SHOW_EXHIBITOR_QUICK_CART_PATH, {
      showId,
      exhibitorPairId,
    })}?${quickOrderIdParamName}=${orderId}`,
    window.location.origin
  ).toString();
}
